import React, { useState } from 'react';
import { Button, ButtonVariant } from './Button';
import CaretDownIcon from './CustomIcons/CaretDownIcon';
import CaretUpIcon from './CustomIcons/CaretUpIcon';
import CheckCircleFilled from './CustomIcons/CheckCircleFilled';
import ErrorCircleFilled from './CustomIcons/ErrorCircleFilled';
import InfoCircleFilled from './CustomIcons/InfoCircleFilled';
import SparkleIcon from './CustomIcons/SparkleIcon';
import WarningCircleFilled from './CustomIcons/WarningCircleFilled';
import MarkdownBlock from './Typography/MarkdownBlock';
import type { TextColor } from './Typography/Text';
import { getTextColor } from './Typography/textStyles';

export type AlertType = 'info' | 'success' | 'error' | 'warning' | 'assistant';

interface AlertButtonConfig {
    label: string;
    onClick: () => void;
    variant?: ButtonVariant;
    icon?: React.ReactNode;
}

interface AlertProps {
    type?: AlertType;
    message: string;
    hasMargin?: boolean;
    className?: string;
    children?: React.ReactNode;
    isExpanded?: boolean;
    onToggle?: (expanded: boolean) => void;
}

// Distinct from the imported `getTextColor`, which maps a TextColor to a CSS variable.
// This maps an AlertType to a TextColor token; both are used in this file.
const getAlertTextColor = (alertType: AlertType): TextColor => {
    switch (alertType) {
        case 'info':
            return 'primary';
        case 'success':
            return 'success';
        case 'error':
            return 'error';
        case 'warning':
            return 'warning';
        // The violet is on `.alert-assistant` itself; inheriting it keeps one definition
        // rather than a TextColor token that only this alert would ever name.
        case 'assistant':
            return 'inherit';
        default:
            return 'primary';
    }
};

const ICON_STYLE = {
    width: '13px',
    height: '13px',
    flexShrink: 0,
};

const AlertIcon = {
    info: (
        <InfoCircleFilled
            style={{ ...ICON_STYLE, color: 'var(--malachite-alert-info-foreground)' }}
        />
    ),
    success: (
        <CheckCircleFilled
            style={{ ...ICON_STYLE, color: 'var(--malachite-alert-success-foreground)' }}
        />
    ),
    error: (
        <ErrorCircleFilled
            style={{ ...ICON_STYLE, color: 'var(--malachite-alert-error-foreground)' }}
        />
    ),
    warning: (
        <WarningCircleFilled
            style={{ ...ICON_STYLE, color: 'var(--malachite-alert-warning-foreground)' }}
        />
    ),
    // The same sparkle the control that produced the message carries, so the two read as
    // one thread rather than two unrelated affordances.
    assistant: (
        <SparkleIcon
            style={{ ...ICON_STYLE, color: 'var(--malachite-alert-assistant-foreground)' }}
        />
    ),
};

const SimpleTextRenderer: React.FC<{ text: string; type: AlertType }> = ({ text, type }) => {
    return <MarkdownBlock text={text} color={getAlertTextColor(type)} size="small" omitMargin />;
};

const Alert: React.FC<AlertProps> = ({
    type = 'info',
    message,
    hasMargin = true,
    className,
    children,
    isExpanded,
    onToggle,
}) => {
    const isExpandable = !!children;
    const [internalExpanded, setInternalExpanded] = useState(false);

    // Use controlled state if isExpanded is provided, otherwise use internal state
    const expanded = isExpanded !== undefined ? isExpanded : internalExpanded;

    const handleToggle = () => {
        const newExpanded = !expanded;
        if (isExpanded === undefined) {
            // Uncontrolled mode
            setInternalExpanded(newExpanded);
        }
        // Call onToggle if provided (for both controlled and uncontrolled)
        onToggle?.(newExpanded);
    };

    const alertClasses = [
        'alert-base',
        `alert-${type}`,
        'flex',
        'w-full', // Ensure full width
        isExpandable && expanded ? 'flex-col' : 'gap-2 items-center min-h-[2rem]',
        !hasMargin ? 'alert-no-margin' : '',
        'text-left',
        isExpandable ? 'rounded-xl !p-0' : '!px-2.5 !py-1.5', // Slightly more vertical padding for centering
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const headerClasses = [
        'flex items-center justify-between w-full',
        isExpandable ? 'gap-2 px-2.5 py-1' : 'gap-2',
        isExpandable && expanded ? 'rounded-t-xl' : isExpandable ? 'rounded-xl' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const expandableContentClasses = ['w-full', 'overflow-hidden'].filter(Boolean).join(' ');

    // Non-expandable alert (original behavior)
    if (!isExpandable) {
        return (
            <div className={alertClasses}>
                {AlertIcon[type]}
                <div className="flex-1 flex items-center">
                    <div className="[&>div]:mb-0">
                        <SimpleTextRenderer text={message} type={type} />
                    </div>
                </div>
            </div>
        );
    }

    // Expandable alert
    return (
        <div className={`${alertClasses} alert-expandable`}>
            {/* Header section with title and expand/collapse button */}
            <div className={headerClasses}>
                <div className="flex-1 flex gap-2 items-center">
                    {AlertIcon[type]}
                    <div className="flex-1 flex items-center">
                        <div className="[&>div]:mb-0">
                            <SimpleTextRenderer text={message} type={type} />
                        </div>
                    </div>
                </div>

                {/* Right side buttons container */}
                <div className="flex items-center gap-2">
                    {/* Expand/collapse button */}
                    <Button
                        variant="no-style"
                        size="xsmall"
                        onClick={handleToggle}
                        aria-label={expanded ? 'Collapse' : 'Expand'}
                        style={{
                            color: getTextColor(getAlertTextColor(type)),
                        }}
                    >
                        {expanded ? (
                            <CaretUpIcon
                                style={{ minWidth: '13px', minHeight: '13px' }}
                            />
                        ) : (
                            <CaretDownIcon
                                style={{ minWidth: '13px', minHeight: '13px' }}
                            />
                        )}
                    </Button>
                </div>
            </div>

            {/* Expandable content */}
            {expanded && (
                <>
                    <div className={expandableContentClasses}>
                        <div className="bg-white rounded-[var(--malachite-radius-10,10px)] shadow-[0px_1px_0px_0px_rgba(0,0,0,0.15)] border border-[#e8e8e8] border-solid mx-2 mb-2 max-h-[553px] overflow-y-auto">
                            {children}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Alert;
export { Alert };
export type { AlertButtonConfig };
